import { useEffect, useMemo, useRef, useState } from 'react';
import { payerSearchApi } from '@features/payer-search/store/api';
import { useAppDispatch } from '@store/hooks';

import type { IntakeRosterPayer } from '../../hooks/useIntakePayerRoster';

/**
 * The app tsconfig types `dispatch` without the injected-endpoint thunk
 * overload, so the subscription is asserted to the documented RTK contract —
 * the same assertion `useMedicalRecordsExport` makes.
 */
interface QuerySubscription<T> {
  unwrap: () => Promise<T>;
  unsubscribe: () => void;
}

interface DirectoryPayer {
  display_name: string;
  stedi_id: string;
  primary_payer_id?: string | null;
}

/** What the payer directory knows about one credentialing record's payer. */
export interface PayerDirectoryIdentity {
  displayName?: string;
  stediId?: string | null;
  primaryPayerId?: string | null;
  /** The lookup came back empty or errored; the record cannot be placed on the roster. */
  failed: boolean;
}

export type PayerIdentityScope = 'admin' | 'provider';

/**
 * Resolve credentialing records' `payer_uuid`s to directory identities.
 *
 * Credentialing records carry only a JotPsych payer-directory UUID, while the
 * intake roster carries a Stedi id and the payer's own id. Placing a record on
 * a roster row therefore needs one directory read per distinct UUID. Those
 * reads run here, in a single effect that accumulates a map, rather than in a
 * per-row hook — a row's identity has to be known before the row is grouped,
 * which is above the row.
 *
 * RTK caches each payer by id, so a UUID already fetched for a name costs
 * nothing, and a UUID is never fetched twice.
 */
export function useCredentialingPayerIdentities(
  uuids: readonly string[],
  scope: PayerIdentityScope,
  enabled = true,
): { identities: Record<string, PayerDirectoryIdentity>; pending: boolean } {
  const dispatch = useAppDispatch();
  const [identities, setIdentities] = useState<Record<string, PayerDirectoryIdentity>>({});
  const requested = useRef(new Set<string>());
  const key = useMemo(() => [...new Set(uuids)].sort().join('|'), [uuids]);
  // The scope decides which authorization the read uses; a staff surface must
  // not fall back to the clinic endpoint, so a scope change starts over.
  useEffect(() => {
    requested.current = new Set();
    setIdentities({});
  }, [scope]);
  useEffect(() => {
    if (!enabled) return;
    const pending = key
      .split('|')
      .filter(uuid => uuid && !requested.current.has(uuid))
      .slice(0, 50);
    if (pending.length === 0) return;
    const endpoint =
      scope === 'admin'
        ? payerSearchApi.endpoints.getV2AdminPayersByPayerId
        : payerSearchApi.endpoints.getV2ProviderPayersByPayerId;
    for (const uuid of pending) requested.current.add(uuid);
    for (const uuid of pending) {
      const request = dispatch(
        endpoint.initiate({ payerId: uuid }),
      ) as unknown as QuerySubscription<DirectoryPayer>;
      // No cancellation guard: `key` changes whenever the record list does, and
      // abandoning an in-flight lookup there would leave its uuid marked as
      // requested but never resolved, stranding that record under "Not on the
      // intake roster" for good. The writes are keyed by uuid and idempotent.
      void request
        .unwrap()
        .then(payer => {
          setIdentities(current => ({
            ...current,
            [uuid]: {
              displayName: payer.display_name,
              stediId: payer.stedi_id,
              primaryPayerId: payer.primary_payer_id ?? null,
              failed: false,
            },
          }));
        })
        .catch(() => {
          // A failed lookup is recorded, not retried silently: the row still
          // renders, under "Not on the intake roster", with its name fallback.
          setIdentities(current => ({ ...current, [uuid]: { failed: true } }));
        })
        .finally(() => {
          request.unsubscribe();
        });
    }
  }, [key, scope, enabled, dispatch]);
  // Derived from the map rather than an in-flight counter: a counter is still
  // zero on the render before the effect runs, and a caller that gates on it
  // would show one frame of records placed as if they were off the roster.
  return {
    identities,
    pending: enabled && key.split('|').some(uuid => uuid && !identities[uuid]),
  };
}

/**
 * The roster payer a directory identity belongs to, or null.
 *
 * Stedi id is the strong join: the roster stores it verbatim from the same
 * directory. `primary_payer_id` is the fallback for roster rows typed in
 * before a Stedi match existed, and it is only consulted when neither side has
 * a Stedi id to disagree about.
 */
export function matchRosterPayer(
  identity: PayerDirectoryIdentity | undefined,
  payers: readonly IntakeRosterPayer[],
): IntakeRosterPayer | null {
  if (!identity || identity.failed) return null;
  if (identity.stediId) {
    const byStedi = payers.find(payer => payer.stediId === identity.stediId);
    if (byStedi) return byStedi;
  }
  if (identity.primaryPayerId) {
    const byPayerId = payers.find(
      payer => !payer.stediId && payer.payerId === identity.primaryPayerId,
    );
    if (byPayerId) return byPayerId;
  }
  return null;
}
